import { Fragment, type ReactElement } from 'react';
import type { MaterialItem, RegionMasterDto } from '../../domain/types';
import { materialLineDetailRows } from '../../domain/types';
import { regionDisplayName } from '../../domain/wellRegions';

export interface RfqMaterialsSnapshotSectionProps {
  materials: MaterialItem[];
  requestNumber: string;
  regionCode: string;
  /** Linked PR document id — must not be the RFQ document id. */
  procurementRequestId: string;
  /** RFQ document id (for guarding against a bad API value that equals RFQ id). */
  rfqDocumentId?: string;
  activeRegions: RegionMasterDto[];
  /** Opens the procurement request (e.g. full-page request URL). */
  onOpenProcurementRequest?: (procurementRequestId: string) => void;
}

export function RfqMaterialsSnapshotSection({
  materials,
  requestNumber,
  regionCode,
  procurementRequestId,
  rfqDocumentId,
  activeRegions,
  onOpenProcurementRequest
}: RfqMaterialsSnapshotSectionProps): ReactElement {
  const prId = procurementRequestId.trim();
  const rfqId = rfqDocumentId?.trim() ?? '';
  const canOpenRequest =
    prId.length > 0 && (!rfqId || prId !== rfqId) && Boolean(onOpenProcurementRequest);

  const regionLabel = regionCode.trim()
    ? regionDisplayName(regionCode.trim(), activeRegions)
    : '—';

  return (
    <div className="full-width rfq-materials-from-pr">
      <section className="rfq-pr-summary" aria-label="Linked procurement request">
        <h5 className="po-subheading rfq-section-title">Procurement request</h5>
        <dl className="detail-list rfq-pr-summary-dl">
          <dt>Request #</dt>
          <dd>
            {requestNumber.trim() || '—'}
            {canOpenRequest ? (
              <>
                {' '}
                <button
                  type="button"
                  className="secondary"
                  onClick={() => onOpenProcurementRequest?.(prId)}
                >
                  Open request
                </button>
              </>
            ) : null}
          </dd>
          <dt>Region</dt>
          <dd>{regionLabel}</dd>
        </dl>
      </section>

      <section className="rfq-materials-block" aria-label="Materials snapshot">
        <div className="rfq-section-header">
          <h5 className="po-subheading rfq-section-title">
            Materials to quote
            {materials.length > 0 ? (
              <span className="rfq-count-badge" aria-hidden="true">
                {materials.length}
              </span>
            ) : null}
          </h5>
        </div>
        <p className="hint full-width rfq-materials-lead">
          Snapshot from the procurement request when this RFQ was created. Add wording for vendors under{' '}
          <strong>Instructions to vendors</strong> below.
        </p>
        {materials.length === 0 ? (
          <p className="hint rfq-empty-hint">No material lines on this quote request.</p>
        ) : (
          <div className="rfq-materials-stack">
            {materials.map((mat, i) => (
              <section
                key={`rfq-mat-${mat.itemId}-${i}`}
                className="nested-card material-line-readonly rfq-material-card"
                aria-label={`Material line ${i + 1}`}
              >
                <h4 className="form-section-title rfq-material-card-title">
                  <span className="rfq-material-line-badge">{i + 1}</span>
                  <span>Material line</span>
                </h4>
                <dl className="detail-list rfq-material-detail-list">
                  {materialLineDetailRows(mat).map((row, ri) => (
                    <Fragment key={`${i}-${ri}-${row.label}`}>
                      <dt>{row.label}</dt>
                      <dd className={row.label === 'Description' ? 'multiline' : undefined}>{row.value}</dd>
                    </Fragment>
                  ))}
                </dl>
              </section>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
